<script lang="ts">
    import { onDestroy } from 'svelte';

    export let name: string;
    export let errors: Record<string, string[]>;
    export let label: string = '';
    export let tags: string[] = [];
    export let placeholder: string = 'Add tag…';
    export let disabled: boolean = false;

    let inputValue = '';
    let inputEl: HTMLInputElement;

    errors[name] = [];

    const addTag = () => {
        const t = inputValue.trim();
        if (t && !tags.includes(t)) {
            tags = [...tags, t];
        }
        inputValue = '';
    };

    const removeTag = (i: number) => {
        tags = tags.filter((_, idx) => idx !== i);
    };

    const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
        } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
            tags = tags.slice(0, -1);
        }
    };

    const focusInput = () => {
        if (!disabled) inputEl.focus();
    };

    onDestroy(() => { errors[name] = []; });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div class="form-field">
    {#if label}<span class="label-text">{label}</span>{/if}
    <div class="tags-box" class:disabled class:error={errors[name].length > 0} on:click={focusInput}>
        {#each tags as tag, i}
            <span class="tag-chip" on:click|stopPropagation>
                {tag}
                {#if !disabled}
                    <button type="button" class="chip-remove" on:click={() => removeTag(i)} aria-label="Remove {tag}">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                {/if}
            </span>
        {/each}
        <input
            type="text"
            {placeholder}
            {disabled}
            bind:value={inputValue}
            bind:this={inputEl}
            on:keydown={handleKeydown}
            on:blur={addTag}
            class="tags-input"
        />
    </div>
    <span class="field-errors">
        {#each errors[name] as err}<span>{err}</span>{/each}
    </span>
</div>

<style lang="scss">
    .tags-box {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5em;
        align-items: center;
        padding: 0.5em 0.75em;
        border: 1px solid $color-border;
        border-radius: $border-radius-medium;
        background-color: transparent;
        cursor: text;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        min-height: 44px;
        box-sizing: border-box;

        &:hover:not(.disabled) {
            border-color: $color-text;
            background-color: rgba(128, 128, 128, 0.03);
        }

        &:focus-within {
            border: 2px solid $color-primary;
            padding: calc(0.5em - 1px) calc(0.75em - 1px);
        }

        &.disabled {
            opacity: 0.38;
            cursor: not-allowed;
            border-color: rgba($color-border, 0.5);
        }

        &.error {
            border-color: $color-danger !important;
        }
    }

    .tag-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.4em;
        padding: 0.35em 0.75em;
        background-color: $color-bg-active;
        border: 1px solid $color-border;
        border-radius: 8px;
        font-size: 0.85em;
        font-weight: 600;
        color: $color-text;
        transition: all 0.15s ease;

        &:hover {
            border-color: $color-text;
        }
    }

    .chip-remove {
        all: unset;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        color: $color-text-secondary;
        cursor: pointer;
        transition: background-color 0.15s ease, color 0.15s ease;

        &:hover { 
            background-color: rgba($color-danger, 0.1);
            color: $color-danger; 
        }
    }

    .tags-input {
        all: unset !important; // Force-remove all global input styles
        flex: 1;
        min-width: 6em;
        border: none !important;
        background: transparent !important;
        padding: 0.25em 0.4em !important;
        margin: 0 !important;
        outline: none !important;
        font-family: inherit;
        font-size: $font-size;
        color: $color-text;

        &::placeholder {
            color: $color-disabled-text;
            opacity: 0.7;
        }
    }
</style>
